import { BaseApiClient } from './base-client';
import type { ApiResponse } from '../../types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Avatar API - методы для генерации говорящих аватаров
 */

export interface TtsGenerateParams {
  text: string;
  voice: string;
  speed?: number;
  teamId?: string;
}

export interface TtsGenerateResponse {
  url: string;
  s3Key: string;
  duration: number;
  charCount: number;
  voice: string;
  format: string;
  tokensDeducted: number;
  newBalance?: number;
}

export interface AvatarGenerateParams {
  prompt: string;
  quality: 'standard' | 'pro';
  audioUrl: string;
  audioS3Key?: string;
  audioDuration: number;
  voiceName?: string;
  ttsText?: string;
  audioSource?: 'tts' | 'upload';
  imageUrl?: string;
  imageS3Key?: string;
  image?: File;
  teamId?: string;
  folderId?: string;
}

export interface GeneratedAvatar {
  id: string;
  user_id: string;
  provider: string;
  model: string;
  prompt: string;
  s3_key: string;
  s3_url: string;
  audio_s3_key?: string;
  audio_url?: string;
  duration: number;
  quality: string;
  ratio: string;
  thumbnail_url?: string;
  thumbnail_s3_key?: string;
  voice_name?: string;
  tts_text?: string;
  generation_options: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  team_id?: string;
  team_folder_id?: string;
  generated_by_user_id?: string;
  // Team mode author info
  author_first_name?: string;
  author_last_name?: string;
  author_username?: string;
  author_photo_url?: string;
}

export interface AvatarGenerateResponse {
  id: string;
  url: string;
  s3Key: string;
  thumbnailUrl: string;
  duration: number;
  quality: string;
  prompt: string;
  voiceName?: string;
  audioSource: string;
  generationTime: number;
  tokensDeducted: number;
  newBalance?: number;
}

export interface AvatarHistoryResponse {
  avatars: GeneratedAvatar[];
  hasMore: boolean;
  totalCount: number;
}

export class AvatarApi extends BaseApiClient {
  
  // ===== TTS GENERATION API =====
  
  /**
   * Генерация TTS аудио из текста
   */
  async generateTts(params: TtsGenerateParams): Promise<ApiResponse<TtsGenerateResponse>> {
    try {
      const response = await this.client.post('/api/avatars/tts/generate', params);
      return response.data;
    } catch (error: any) {
      console.error('Error generating TTS:', error);
      throw error;
    }
  }
  
  // ===== AVATAR GENERATION API =====
  
  /**
   * Генерация видео аватара
   */
  async generateAvatar(params: AvatarGenerateParams): Promise<ApiResponse<AvatarGenerateResponse>> {
    try {
      const teamId = sanitizeTeamId(params.teamId);
      // Создаем FormData для отправки файлов
      const formData = new FormData();

      // Добавляем обычные параметры
      formData.append('prompt', params.prompt);
      formData.append('quality', params.quality);
      formData.append('audioUrl', params.audioUrl);
      formData.append('audioDuration', String(params.audioDuration));

      if (params.audioS3Key) formData.append('audioS3Key', params.audioS3Key);
      if (params.voiceName) formData.append('voiceName', params.voiceName);
      if (params.ttsText) formData.append('ttsText', params.ttsText);
      if (params.audioSource) formData.append('audioSource', params.audioSource);
      if (teamId) formData.append('teamId', teamId);
      if (params.folderId) formData.append('folderId', params.folderId);
      
      // Добавляем изображение
      if (params.image) {
        formData.append('image', params.image);
      } else if (params.imageUrl) {
        formData.append('imageUrl', params.imageUrl);
        if (params.imageS3Key) {
          formData.append('imageS3Key', params.imageS3Key);
        }
      }
      
      const response = await this.client.post('/api/avatars/generate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000 // 10 минут для генерации
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Error generating avatar:', error);
      throw error;
    }
  }
  
  /**
   * Проверка статуса генерации аватара
   */
  async getGenerationStatus(generationId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/api/avatars/generate/status', {
        params: { generationId }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting avatar generation status:', error);
      throw error;
    }
  }
  
  // ===== AVATAR HISTORY API =====
  
  /**
   * Загрузка истории аватаров
   */
  async loadAvatarHistory(
    limit: number = 50,
    offset: number = 0,
    rawTeamId?: string,
    folderId?: string,
    memberFilter?: string
  ): Promise<ApiResponse<AvatarHistoryResponse>> {
    try {
      const teamId = sanitizeTeamId(rawTeamId);
      const params: Record<string, any> = { limit, offset };
      if (teamId) params.teamId = teamId;
      if (folderId) params.folderId = folderId;
      if (memberFilter) params.memberFilter = memberFilter;
      
      const response = await this.client.get('/api/avatars/history', { params });
      return response.data;
    } catch (error: any) {
      console.error('Error loading avatar history:', error);
      throw error;
    }
  }
  
  /**
   * Удаление аватара
   */
  async deleteAvatar(avatarId: string): Promise<ApiResponse<{ success: boolean }>> {
    try {
      const response = await this.client.delete(`/api/avatars/${avatarId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting avatar:', error);
      throw error;
    }
  }
  
  // ===== ACTIVE GENERATIONS API =====
  
  /**
   * Получение активных генераций аватаров
   */
  async getActiveGenerations(rawTeamId?: string): Promise<ApiResponse<any>> {
    try {
      const teamId = sanitizeTeamId(rawTeamId);
      const params: Record<string, any> = {};
      if (teamId) params.teamId = teamId;
      
      const response = await this.client.get('/api/avatars/active-generations', { params });
      return response.data;
    } catch (error: any) {
      console.error('Error getting active avatar generations:', error);
      throw error;
    }
  }
  
  // ===== COMPOSER ATTACHMENTS API =====
  
  /**
   * Upload image to S3 for avatar generation
   */
  async uploadImage(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ s3Key: string; url: string; dbId?: string }>> {
    try {
      // Get presigned URL
      const presignedResponse = await this.client.post('/api/upload/presigned-url', {
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        folder: 'avatars/inputs'
      });

      const { presignedUrl, s3Key, downloadUrl } = presignedResponse.data;

      // Upload to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(Math.max(1, progress));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress?.(100);
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Save to composer attachments DB
      const saveResponse = await this.client.post('/api/avatars/composer/image', {
        fileName: file.name,
        fileType: 'image',
        mimeType: file.type,
        fileSize: file.size,
        s3Key,
        s3Url: downloadUrl
      });

      return {
        success: true,
        data: {
          s3Key,
          url: downloadUrl,
          dbId: saveResponse.data?.attachment?.id
        }
      };
    } catch (error: any) {
      console.error('Error uploading avatar image:', error);
      throw error;
    }
  }
  
  /**
   * Upload audio file to S3 for avatar generation
   */
  async uploadAudio(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ s3Key: string; url: string; dbId?: string }>> {
    try {
      // Get presigned URL
      const presignedResponse = await this.client.post('/api/upload/presigned-url', {
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        folder: 'avatars/audio'
      });

      const { presignedUrl, s3Key, downloadUrl } = presignedResponse.data;

      // Upload to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(Math.max(1, progress));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress?.(100);
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Save to composer attachments DB
      const saveResponse = await this.client.post('/api/avatars/composer/audio', {
        fileName: file.name,
        fileType: 'audio',
        mimeType: file.type,
        fileSize: file.size,
        s3Key,
        s3Url: downloadUrl
      });

      return {
        success: true,
        data: {
          s3Key,
          url: downloadUrl,
          dbId: saveResponse.data?.attachment?.id
        }
      };
    } catch (error: any) {
      console.error('Error uploading avatar audio:', error);
      throw error;
    }
  }
  
  /**
   * Get saved composer attachments (image and audio)
   */
  async getComposerAttachments(): Promise<ApiResponse<{
    image?: { s3Key: string; url: string; dbId: string };
    audio?: { s3Key: string; url: string; dbId: string; type: 'tts' | 'upload'; voiceName?: string; ttsText?: string; duration?: number };
  }>> {
    try {
      const response = await this.client.get('/api/avatars/composer');
      return response.data;
    } catch (error: any) {
      console.error('Error getting composer attachments:', error);
      throw error;
    }
  }
  
  /**
   * Delete composer image attachment
   */
  async deleteComposerImage(dbId: string, s3Key: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.delete(`/api/avatars/composer/image/${dbId}`, {
        data: { s3Key }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting composer image:', error);
      throw error;
    }
  }
  
  /**
   * Delete composer audio attachment
   */
  async deleteComposerAudio(dbId: string, s3Key: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.delete(`/api/avatars/composer/audio/${dbId}`, {
        data: { s3Key }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error deleting composer audio:', error);
      throw error;
    }
  }
  
  /**
   * Save TTS audio to composer (after generation)
   */
  async saveGeneratedAudioToComposer(audioData: {
    s3Key: string;
    url: string;
    voiceName: string;
    ttsText: string;
    duration: number;
  }): Promise<ApiResponse<{ dbId: string }>> {
    try {
      const response = await this.client.post('/api/avatars/composer/audio', {
        fileType: 'tts',
        s3Key: audioData.s3Key,
        s3Url: audioData.url,
        voiceName: audioData.voiceName,
        ttsText: audioData.ttsText,
        duration: audioData.duration
      });
      return response.data;
    } catch (error: any) {
      console.error('Error saving TTS audio to composer:', error);
      throw error;
    }
  }
  
  /**
   * Clear all composer attachments
   */
  async clearComposerAttachments(): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.delete('/api/avatars/composer');
      return response.data;
    } catch (error: any) {
      console.error('Error clearing composer attachments:', error);
      throw error;
    }
  }
  
  // ===== VOICE PREVIEWS API (Admin) =====
  
  /**
   * Get all voice previews
   */
  async getVoicePreviews(): Promise<ApiResponse<{ previews: Record<string, { url: string; s3Key: string; duration?: number }> }>> {
    try {
      const response = await this.client.get('/api/avatars/tts/voice-previews');
      return response.data;
    } catch (error: any) {
      console.error('Error getting voice previews:', error);
      throw error;
    }
  }
  
  /**
   * Upload voice preview (admin only)
   * Replaces existing preview if one exists
   */
  async uploadVoicePreview(
    voiceName: string,
    file: File
  ): Promise<ApiResponse<{ voiceName: string; url: string; s3Key: string; replaced: boolean }>> {
    try {
      const formData = new FormData();
      formData.append('audio', file);
      
      const response = await this.client.post(
        `/api/avatars/tts/voice-preview/${encodeURIComponent(voiceName)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error uploading voice preview:', error);
      throw error;
    }
  }
  
  /**
   * Delete voice preview (admin only)
   */
  async deleteVoicePreview(voiceName: string): Promise<ApiResponse<{ voiceName: string; deleted: boolean }>> {
    try {
      const response = await this.client.delete(`/api/avatars/tts/voice-preview/${encodeURIComponent(voiceName)}`);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting voice preview:', error);
      throw error;
    }
  }
}

